import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { formatHistoryEventLine, getVisibleHistoryEvents } from '../utils/commissionHistory';
import { getCommissionHistory } from '../queries/commissions';
import { USER_ROLES } from '../constants';

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `${value}%`;
};

const CommissionHistoryModal = ({ open, onClose, commission, userRole }) => {
  const commissionId = commission?._id;
  const { data: historyData, isLoading, isFetching, isError, error, refetch } = useQuery(
    getCommissionHistory({
      id: commissionId,
      config: {
        enabled: Boolean(open && commissionId),
        staleTime: 0,
        refetchOnMount: 'always',
      },
    }),
  );

  useEffect(() => {
    if (open && commissionId) {
      refetch();
    }
  }, [open, commissionId, refetch]);

  const history = getVisibleHistoryEvents(historyData, userRole);
  const snapshot = commission?.commissionPlanSnapshot;
  const isClient = userRole === USER_ROLES.CLIENT;
  const showLoader = isLoading || (isFetching && !historyData);

  const commissionType = snapshot?.commissionType || '';
  const showRecurring =
    commissionType.includes('Recurring') || commissionType === 'Event-Based Commission + Recurring Revenue Commission';
  const showEventBased =
    commissionType.includes('Event-Based') || commissionType === 'Event-Based Commission + Recurring Revenue Commission';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Commission History</DialogTitle>
      <DialogContent dividers>
        {showLoader ? (
          <Box display='flex' justifyContent='center' py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : isError ? (
          <Typography variant='body2' color='error'>
            {error?.response?.data?.message || error?.message || 'Failed to load commission history.'}
          </Typography>
        ) : history.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No history available for this commission.
          </Typography>
        ) : (
          <Box component='ul' sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
            {history.map((event, index) => (
              <Box
                component='li'
                key={event._id || `${event.action}-${event.timestamp}-${index}`}
                sx={{ mb: 1.25, pl: 0.5 }}
              >
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {formatHistoryEventLine(event)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          Applicable Commission Plan
        </Typography>
        <Typography variant='body2' color='text.secondary' mb={2}>
          This is the applicable commission plan at the moment of the creation of this commission.
        </Typography>

        {!snapshot ? (
          <Typography variant='body2' color='text.secondary'>
            No commission plan snapshot available.
          </Typography>
        ) : (
          <Box>
            {snapshot.productName ? (
              <Typography variant='body2' mb={1.5}>
                Product: {snapshot.productName}
              </Typography>
            ) : null}

            {showRecurring || !commissionType ? (
              <Box mb={2}>
                <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                  Recurring Revenue
                </Typography>
                <Typography variant='body2'>First Term (%): {formatPercent(snapshot.commissionFirstTerm)}</Typography>
                <Typography variant='body2'>Renewals (%): {formatPercent(snapshot.commissionRenewals)}</Typography>
                <Typography variant='body2'>Duration (Months): {snapshot.duration || '—'}</Typography>
              </Box>
            ) : null}

            {showEventBased || !commissionType ? (
              <Box mb={2}>
                <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                  Event-Based Commission
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {snapshot.oneOffCommissions || '—'}
                </Typography>
              </Box>
            ) : null}

            <Box>
              <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                Buyout Options
              </Typography>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {snapshot.buyOutOptions || '—'}
              </Typography>
            </Box>

            {!isClient && commissionType ? (
              <Typography variant='caption' color='text.secondary' display='block' mt={2}>
                Type: {commissionType}
              </Typography>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='contained'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionHistoryModal;
